/**
 * /admin/leads — admin-side read-only view of detected leads.
 *
 * Shows what the pipeline (keyword pre-filter → Claude judge) has produced.
 * Click a row to see full LLM judgment JSON in a side panel.
 *
 * Sales-side claim/release/close UX lives elsewhere (Phase 7).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  type GroupSummary,
  type LeadDetail,
  type LeadSummary,
  type LeadTriggersResponse,
  Routes,
} from '@tg-leads/contracts';

import {
  LeadFilterChips,
  readSelectionFromSearch,
  writeSelectionToSearch,
} from '../../components/LeadFilterChips';
import { api } from '../../lib/api';

interface LeadList {
  items: LeadSummary[];
  nextCursor: string | null;
}
interface GroupList {
  items: GroupSummary[];
  nextCursor: string | null;
}

const statusBadge = (s: LeadSummary['status']) => {
  switch (s) {
    case 'detected':
      return 'badge-warn';
    case 'queued':
    case 'contacted':
    case 'replied':
    case 'assigned':
    case 'engaging':
      return 'badge-ok';
    case 'closed_won':
      return 'badge-ok';
    case 'closed_lost':
      return 'badge-fail';
    default:
      return 'badge-neutral';
  }
};

const statusLabel = (s: LeadSummary['status']): string => {
  switch (s) {
    case 'detected':
      return '待认领';
    case 'queued':
      return '待自动外联';
    case 'contacted':
      return '已发送等回复';
    case 'replied':
      return '对方已回复';
    case 'assigned':
      return '跟进中';
    case 'engaging':
      return '沟通中';
    case 'closed_won':
      return '已成单';
    case 'closed_lost':
      return '未成单';
    default:
      return s;
  }
};

export function LeadsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useSearchParams();
  const selection = useMemo(() => readSelectionFromSearch(search), [search]);

  const leadsUrl = useMemo(() => {
    const u = new URL(Routes.admin.leads, window.location.origin);
    if (selection.categories.length > 0)
      u.searchParams.set('categories', selection.categories.join(','));
    if (selection.tags.length > 0)
      u.searchParams.set('tags', selection.tags.join(','));
    return u.pathname + (u.search ? u.search : '');
  }, [selection]);

  const leads = useQuery({
    queryKey: ['leads', selection.categories, selection.tags],
    queryFn: () => api.get<LeadList>(leadsUrl),
    refetchInterval: 5_000,
  });

  // Cross-ref group titles so the table shows "Okpay USDT..." not a UUID.
  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<GroupList>(Routes.admin.groups),
  });
  const groupTitle = useMemo(() => {
    const map = new Map<string, string>();
    (groups.data?.items ?? []).forEach((g) => map.set(g.id, g.title));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  }, [groups.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">全部线索（只读）</h1>
        <p className="text-sm text-slate-500 mt-1">
          这里看所有租户内的线索。管线走的是：原始消息 → 关键词过滤 → 三层模型判断 → 创建线索。每 5 秒自动刷新。
          认领 / 释放 / 成单 等动作在销售工作台做。
        </p>
      </div>

      <LeadFilterChips
        statsUrl={Routes.admin.leadTagStats}
        selection={selection}
        onChange={(next) => setSearch(writeSelectionToSearch(search, next))}
      />

      <div className="card overflow-x-auto">
        {leads.isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : !leads.data?.items.length ? (
          <div className="text-slate-500 text-sm">
            还没有线索。如果你刚配完"业务描述与阈值"，等下一波群消息进来，或在群里发一条像
            "求购 USDT" 这样的话试试。
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase border-b">
                <th className="py-2 pr-4">识别时间</th>
                <th className="py-2 pr-4">目标用户</th>
                <th className="py-2 pr-4">类目 / 标签</th>
                <th className="py-2 pr-4">来源群</th>
                <th className="py-2 pr-4">状态</th>
                <th className="py-2 pr-4">分配给</th>
              </tr>
            </thead>
            <tbody>
              {leads.data.items.map((l) => (
                <tr
                  key={l.id}
                  className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedId(l.id)}
                >
                  <td className="py-3 pr-4 text-xs text-slate-600">
                    {new Date(l.createdAt).toLocaleString()}
                  </td>
                  <td className="py-3 pr-4 font-mono">{l.targetUserId}</td>
                  <td className="py-3 pr-4">
                    <div className="flex flex-wrap gap-1 max-w-[20rem]">
                      {l.category && (
                        <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
                          {l.category}
                        </span>
                      )}
                      {l.tags.slice(0, 5).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
                        >
                          {t}
                        </span>
                      ))}
                      {l.tags.length > 5 && (
                        <span className="text-[10px] text-slate-400">
                          +{l.tags.length - 5}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 pr-4 truncate max-w-[14rem]">
                    {groupTitle(l.sourceGroupId)}
                  </td>
                  <td className="py-3 pr-4">
                    <span className={statusBadge(l.status)}>{statusLabel(l.status)}</span>
                  </td>
                  <td className="py-3 pr-4 text-xs text-slate-500">
                    {l.assignedSalesId ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId && (
        <LeadDetailDrawer id={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

function LeadDetailDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const lead = useQuery({
    queryKey: ['lead', id],
    queryFn: () => api.get<LeadDetail>(Routes.admin.lead(id)),
  });
  const triggers = useQuery({
    queryKey: ['lead', id, 'triggers'],
    queryFn: () => api.get<LeadTriggersResponse>(Routes.admin.leadTriggers(id)),
  });

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <aside
        className="absolute right-0 top-0 h-full w-[640px] bg-white shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <h2 className="text-lg font-semibold">线索详情</h2>
            <button
              className="text-slate-400 hover:text-slate-700 text-xl leading-none"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          {lead.isLoading ? (
            <div className="text-slate-500 text-sm">加载中…</div>
          ) : lead.data ? (
            <>
              <section className="border rounded-lg p-4 space-y-2 text-sm">
                <Row k="线索 ID" v={lead.data.id} />
                <Row k="目标用户" v={lead.data.targetUserId} />
                <Row k="状态" v={statusLabel(lead.data.status)} />
                <Row k="版本号" v={String(lead.data.version)} />
                <Row
                  k="识别时间"
                  v={new Date(lead.data.createdAt).toLocaleString()}
                />
              </section>
              <section className="border rounded-lg p-4 space-y-2">
                <h3 className="font-medium text-slate-700">模型判定结果（原始 JSON）</h3>
                <pre className="text-xs whitespace-pre-wrap break-words bg-slate-50 p-3 rounded">
                  {JSON.stringify(lead.data.llmJudgmentJson, null, 2)}
                </pre>
              </section>
              <section className="border rounded-lg p-4 space-y-3">
                <h3 className="font-medium text-slate-700">
                  触发消息（{lead.data.triggerMessageIds.length} 条）
                </h3>
                {triggers.isLoading ? (
                  <div className="text-xs text-slate-500">加载中…</div>
                ) : !triggers.data?.items.length ? (
                  <div className="text-xs text-slate-500">
                    原始消息已被定期清理，或暂时找不到
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {triggers.data.items.map((m) => (
                      <li
                        key={m.id}
                        className="border-l-2 border-indigo-300 pl-3 py-1"
                      >
                        <div className="text-[11px] text-slate-500 flex gap-3 mb-1">
                          <span className="font-mono">
                            {m.senderUsername
                              ? `@${m.senderUsername}${m.senderFirstName ? ` · ${m.senderFirstName}` : ''} (id ${m.senderUserId})`
                              : m.senderFirstName
                                ? `${m.senderFirstName} (id ${m.senderUserId})`
                                : `id ${m.senderUserId}`}
                          </span>
                          <span>{new Date(m.ts).toLocaleString()}</span>
                        </div>
                        <div className="text-sm whitespace-pre-wrap break-words">
                          {m.text}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex">
      <div className="w-24 text-slate-500">{k}</div>
      <div className="flex-1 font-mono break-all">{v}</div>
    </div>
  );
}
